/**
 * Remote Storage Provider (Hosted Backend)
 * Implementation for paid tier users - stores data on remote server
 *
 * STATUS: 🚧 Not yet implemented (stub only)
 *
 * IMPLEMENTATION GUIDE: See /REMOTE_STORAGE_IMPLEMENTATION.md for complete documentation
 *
 * Backend Options:
 * 1. Cloudflare Workers + D1 (Recommended) - Serverless, SQLite-compatible, free tier
 * 2. Supabase - Complete BaaS with auth, PostgreSQL, real-time sync
 * 3. Custom Backend - Full control with Node.js/Express/Hono + PostgreSQL
 *
 * Key Requirements:
 * - Implement all StorageProvider interface methods (see StorageProvider.ts)
 * - Use same database schema as LocalStorageProvider (see src/lib/migrations/)
 * - Add authentication (JWT or API keys)
 * - Implement caching for offline support
 * - Handle error retries and conflict resolution
 *
 * Migration Path:
 * - Users can export from LocalStorageProvider → import to RemoteStorageProvider
 * - Gradual sync in batches for large datasets
 * - See REMOTE_STORAGE_IMPLEMENTATION.md for detailed migration steps
 */

import type { StorageProvider, StorageConfig } from './StorageProvider';
import type {
  Transaction,
  TransactionUpdate,
  SplitInfo,
  ProcessedFile,
  Rule,
  RuleCreateData,
  SaveTransactionsResult,
  BulkUpdateResult,
  SplitTransactionResult,
  UnsplitTransactionResult,
  ApplyRuleResult,
  RuleExistsResult,
  RuleMatchResult
} from '../types';
import type {
  Budget,
  BudgetData,
  BudgetSettings,
  BudgetStatus,
  BudgetCreateData,
  BudgetUpdateData
} from '../types/budget';

/**
 * Remote Storage Provider using hosted backend
 * Factory function creates a stub implementation for future paid tier features
 */
export function createRemoteStorageProvider(config: StorageConfig): StorageProvider {
  if (!config.apiEndpoint || !config.apiKey) {
    throw new Error('RemoteStorageProvider requires apiEndpoint and apiKey');
  }

  const apiEndpoint = config.apiEndpoint;
  const apiKey = config.apiKey;
  let initialized = false;

  function notImplemented(method: string): never {
    throw new Error(`RemoteStorageProvider.${method}() is not yet implemented. This feature is coming soon for paid tier users.`);
  }

  return {
    type: 'remote' as const,

    // Initialization
    initialize: async () => {
      // TODO: Implement authentication and connection check
      // 1. Validate API key by calling /auth/verify endpoint
      // 2. Check API connectivity
      // 3. Load initial cache (user profile, preferences)
      // 4. Set up sync listeners if implementing real-time updates
      // See REMOTE_STORAGE_IMPLEMENTATION.md for examples
      console.warn('RemoteStorageProvider is not yet implemented - using stub');
      initialized = true;
    },

    isInitialized: () => initialized,

    // ==================== STUB METHODS ====================
    // All methods below throw "not implemented" errors
    // Implement these when adding paid tier backend

    getTransactions: async () => {
      notImplemented('getTransactions');
    },

    saveTransactionsForFile: async (_csvFilename: string, _transactions: Transaction[]) => {
      notImplemented('saveTransactionsForFile');
    },

    upsertTransaction: async (_transaction: Transaction) => {
      notImplemented('upsertTransaction');
    },

    bulkUpdateTransactions: async (_description: string, _updates: TransactionUpdate) => {
      notImplemented('bulkUpdateTransactions');
    },

    undoBulkUpdate: async (_previousStates: Transaction[]) => {
      notImplemented('undoBulkUpdate');
    },

    splitTransaction: async (_transactionId: string, _splits: SplitInfo[]) => {
      notImplemented('splitTransaction');
    },

    unsplitTransaction: async (_transactionId: string) => {
      notImplemented('unsplitTransaction');
    },

    deleteAllTransactions: async () => {
      notImplemented('deleteAllTransactions');
    },

    deleteTransactionsFromFile: async (_filename: string) => {
      notImplemented('deleteTransactionsFromFile');
    },

    getProcessedFiles: async () => {
      notImplemented('getProcessedFiles');
    },

    markFileAsProcessed: async (_filename: string, _transactionCount: number, _fileHash?: string, _metadata?: { bank?: string; person?: string; accountName?: string; accountType?: string }) => {
      notImplemented('markFileAsProcessed');
    },

    isFileProcessed: async (_filename: string) => {
      notImplemented('isFileProcessed');
    },

    isFileHashProcessed: async (_fileHash: string) => {
      notImplemented('isFileHashProcessed');
    },

    deleteProcessedFile: async (_filename: string) => {
      notImplemented('deleteProcessedFile');
    },

    getCategories: async () => {
      notImplemented('getCategories');
    },

    addCategory: async (_category: string) => {
      notImplemented('addCategory');
    },

    updateCategory: async (_oldName: string, _newName: string) => {
      notImplemented('updateCategory');
    },

    deleteCategory: async (_name: string, _replacementCategory?: string) => {
      notImplemented('deleteCategory');
    },

    // ==================== CATEGORY ALIASES ====================
    resolveCategoryAlias: async (_categoryName: string) => {
      notImplemented('resolveCategoryAlias');
    },

    getCategoryAliases: async () => {
      notImplemented('getCategoryAliases');
    },

    getInstitutions: async () => {
      notImplemented('getInstitutions');
    },

    getPeople: async () => {
      notImplemented('getPeople');
    },

    getReviewableTransactions: async () => {
      notImplemented('getReviewableTransactions');
    },

    getRules: async () => {
      notImplemented('getRules');
    },

    createRule: async (
      _pattern: string,
      _category: string,
      _person: string | null,
      _options: Partial<RuleCreateData>
    ) => {
      notImplemented('createRule');
    },

    createRulesFromReview: async (_decisions: Array<{
      description: string;
      category: string;
      person?: string | null;
    }>) => {
      notImplemented('createRulesFromReview');
    },

    updateRule: async (_ruleId: string, _updates: Partial<RuleCreateData>) => {
      notImplemented('updateRule');
    },

    deleteRule: async (_ruleId: string) => {
      notImplemented('deleteRule');
    },

    deleteAllRules: async () => {
      notImplemented('deleteAllRules');
    },

    applyRuleToExisting: async (_ruleId: string, _forceApply: boolean) => {
      notImplemented('applyRuleToExisting');
    },

    checkRuleExists: async (_pattern: string) => {
      notImplemented('checkRuleExists');
    },

    applyRulesToTransaction: async (_description: string) => {
      notImplemented('applyRulesToTransaction');
    },

    getBudgets: async () => {
      notImplemented('getBudgets');
    },

    createBudget: async (_data: BudgetCreateData) => {
      notImplemented('createBudget');
    },

    updateBudget: async (_name: string, _type: string, _updates: BudgetUpdateData) => {
      notImplemented('updateBudget');
    },

    deleteBudget: async (_name: string, _type: string) => {
      notImplemented('deleteBudget');
    },

    deleteAllBudgets: async () => {
      notImplemented('deleteAllBudgets');
    },

    getBudgetStatus: async (_month: string) => {
      notImplemented('getBudgetStatus');
    },

    updateBudgetRollover: async (_name: string, _type: string, _rolloverAmount: number) => {
      notImplemented('updateBudgetRollover');
    },

    resetBudgetRollover: async (_name: string, _type: string) => {
      notImplemented('resetBudgetRollover');
    },

    getBudgetSettings: async () => {
      notImplemented('getBudgetSettings');
    },

    updateBudgetSettings: async (_settings: Partial<BudgetSettings>) => {
      notImplemented('updateBudgetSettings');
    },

    dismissRecurringPattern: async (_pattern: string) => {
      notImplemented('dismissRecurringPattern');
    },

    getDismissedPatterns: async () => {
      notImplemented('getDismissedPatterns');
    },

    undismissRecurringPattern: async (_pattern: string) => {
      notImplemented('undismissRecurringPattern');
    },

    snoozeRecurringPattern: async (_pattern: string, _days: number) => {
      notImplemented('snoozeRecurringPattern');
    },

    getSnoozedPatterns: async () => {
      notImplemented('getSnoozedPatterns');
    },

    updateRecurringInterval: async (_pattern: string, _customInterval: number) => {
      notImplemented('updateRecurringInterval');
    },

    getRecurringMetadata: async (_pattern: string) => {
      notImplemented('getRecurringMetadata');
    },

    // ==================== USER ACTIONS (PREFERENCES, DISMISSALS, UI STATE) ====================
    setUserPreference: async (_key: string, _value: string) => {
      notImplemented('setUserPreference');
    },

    getUserPreference: (_key: string) => {
      notImplemented('getUserPreference');
    },

    getAllUserPreferences: () => {
      notImplemented('getAllUserPreferences');
    },

    deleteUserPreference: async (_key: string) => {
      notImplemented('deleteUserPreference');
    },

    dismissItem: async (_key: string, _days?: number) => {
      notImplemented('dismissItem');
    },

    isDismissed: (_key: string) => {
      notImplemented('isDismissed');
    },

    getDismissedItems: () => {
      notImplemented('getDismissedItems');
    },

    getDismissedItemsWithExpiry: () => {
      notImplemented('getDismissedItemsWithExpiry');
    },

    undismissItem: async (_key: string) => {
      notImplemented('undismissItem');
    },

    setUIState: async (_key: string, _value: string) => {
      notImplemented('setUIState');
    },

    getUIState: (_key: string) => {
      notImplemented('getUIState');
    },

    getAllUIState: () => {
      notImplemented('getAllUIState');
    },

    deleteUIState: async (_key: string) => {
      notImplemented('deleteUIState');
    },

    clearAllUIState: async () => {
      notImplemented('clearAllUIState');
    },

    clearExpiredActions: async () => {
      notImplemented('clearExpiredActions');
    }
  };
}

/**
 * Example implementation guide for future development:
 *
 * getTransactions: async () => {
 *   const response = await fetch(`${apiEndpoint}/transactions`, {
 *     headers: {
 *       'Authorization': `Bearer ${apiKey}`,
 *       'Content-Type': 'application/json'
 *     }
 *   });
 *
 *   if (!response.ok) {
 *     throw new Error('Failed to fetch transactions');
 *   }
 *
 *   const data = await response.json();
 *   return data.transactions;
 * }
 */
